import React, { useState, useEffect } from "react";
import { baseUrl, fmtMoney, toCsv, downloadCsv } from "../shared.js";
import { StatusChip } from "./ui";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import moment from "moment";
import { Badge, Box } from "@mui/material";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

function TotalRow({ label, value, strong }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid var(--border)" }}>
      <Typography sx={{ fontWeight: strong ? 700 : 500, color: strong ? "var(--text)" : "var(--muted)" }}>{label}</Typography>
      <Typography className="app-mono" sx={{ fontWeight: 700, color: strong ? "var(--primary)" : "var(--text)" }}>{value}</Typography>
    </Box>
  );
}

function ViewMyTimesheets() {
  const [createTimesheetOpen, setCreateTimesheetOpen] = useState(false);
  const [confirmDeleteBoxOpen, setConfirmDeleteBoxOpen] = useState(false);
  const [confirmUpdateBoxOpen, setConfirmUpdateBoxOpen] = useState(false);
  const [confirmMessagePopup, setConfirmMessagePopup] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");

  const [totals, setTotals] = useState([]);
  const [payrollTotals, setPayrollTotals] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [payouts, setPayouts] = useState([]);

  const [taskListLoaded, setTaskListLoaded] = useState(false);
  const [payoutListLoaded, setPayoutListLoaded] = useState(false);

  const [timesheetData, setTimeSheetData] = useState();
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));

  const [taskDates, setTaskDates] = useState(new Set());
  const [payoutDates, setPayoutDates] = useState(new Set());

  const headers = () => ({
    "Content-Type": "application/json",
    username: localStorage.getItem("username"),
    bToken: localStorage.getItem("bToken"),
  });

  // ---------------- API calls (unchanged endpoints) ----------------

  const getTotals = () => {
    fetch(baseUrl + "pandt/ownTotals", { method: "POST", mode: "cors", headers: headers() })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) setTotals(data.result || []); })
      .catch(console.log);
  };

  const getPayrollTotals = () => {
    fetch(baseUrl + "payroll/ownTotals", { method: "POST", mode: "cors", headers: headers() })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) setPayrollTotals(data.result || []); })
      .catch(console.log);
  };

  const getTaskDates = () => {
    fetch(baseUrl + "tasks/distinctDate ", { method: "POST", mode: "cors", headers: headers() })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) setTaskDates(new Set((data.result || []).map((d) => d.taskDate))); })
      .catch(console.log);
  };

  const getPayoutDates = () => {
    fetch(baseUrl + "payouts/distinctDate ", { method: "POST", mode: "cors", headers: headers() })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) setPayoutDates(new Set((data.result || []).map((d) => d.payoutDate))); })
      .catch(console.log);
  };

  const getTasks = () => {
    fetch(baseUrl + "tasks/ownList ", { method: "POST", mode: "cors", headers: headers() })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) { setTasks(data.result || []); setTaskListLoaded(true); } })
      .catch(console.log);
  };

  const getPayouts = () => {
    fetch(baseUrl + "payouts/ownList", { method: "POST", mode: "cors", headers: headers() })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) { setPayouts(data.result || []); setPayoutListLoaded(true); } })
      .catch(console.log);
  };

  const getDateTotals = (e) => {
    fetch(baseUrl + "pandt/ownTotals/forDate", { method: "POST", mode: "cors", headers: headers(), body: JSON.stringify({ selectedDate: e || selectedDate }) })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) setTotals(data.result || []); })
      .catch(console.log);
  };

  const getDateTasks = (e) => {
    fetch(baseUrl + "tasks/ownList/forDate ", { method: "POST", mode: "cors", headers: headers(), body: JSON.stringify({ selectedDate: e || selectedDate }) })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) { setTasks(data.result || []); setTaskListLoaded(true); } })
      .catch(console.log);
  };

  const getDatePayouts = (e) => {
    fetch(baseUrl + "payouts/ownList/forDate", { method: "POST", mode: "cors", headers: headers(), body: JSON.stringify({ selectedDate: e || selectedDate }) })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) { setPayouts(data.result || []); setPayoutListLoaded(true); } })
      .catch(console.log);
  };

  const refreshAll = () => {
    getDateTasks(); getDatePayouts(); getDateTotals(); getPayrollTotals(); getPayoutDates(); getTaskDates();
  };

  const createTimesheet = () => {
    fetch(baseUrl + "task/create", { method: "POST", mode: "cors", headers: headers(), body: JSON.stringify(timesheetData) })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) { setConfirmMessage(data.message); openConfirmMessage(); openCreateTimesheet(); refreshAll(); } })
      .catch(console.log);
  };

  const updateTimesheet = () => {
    fetch(baseUrl + "task/update", { method: "POST", mode: "cors", headers: headers(), body: JSON.stringify(timesheetData) })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) { setConfirmMessage(data.message); openConfirmMessage(); openConfirmUpdateBox(); openCreateTimesheet(); refreshAll(); } })
      .catch(console.log);
  };

  const deleteTimesheet = () => {
    fetch(baseUrl + "task/delete", { method: "POST", mode: "cors", headers: headers(), body: JSON.stringify(timesheetData) })
      .then((r) => r.json())
      .then((data) => { if (data.status !== 401) { setConfirmMessage(data.message); openConfirmMessage(); openConfirmDeleteBox(); openCreateTimesheet(); refreshAll(); } })
      .catch(console.log);
  };

  // ---------------- Dialog toggles ----------------
  const openConfirmMessage = () => setConfirmMessagePopup((v) => !v);
  const openCreateTimesheet = () => setCreateTimesheetOpen((v) => !v);
  const openConfirmDeleteBox = () => setConfirmDeleteBoxOpen((v) => !v);
  const openConfirmUpdateBox = () => setConfirmUpdateBoxOpen((v) => !v);

  // ---------------- Initial fetches ----------------
  useEffect(() => {
    const today = dayjs().format("YYYY-MM-DD");
    setSelectedDate(today);
    getDateTotals(today); getPayrollTotals(); getDateTasks(today); getDatePayouts(today);
    getTaskDates(); getPayoutDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------- Calendar day with two dots ----------------
  const CustomDay = React.memo((props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const dateStr = dayjs(day).format("YYYY-MM-DD");
    const isTask = taskDates.has(dateStr);
    const isPayout = payoutDates.has(dateStr);
    return (
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ "& .MuiBadge-badge": { pointerEvents: "none" } }}
        badgeContent={
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5, mt: "1px", minWidth: 10 }}>
            <Box sx={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", bgcolor: isTask ? "primary.main" : "transparent", pointerEvents: "none" }} />
            <Box sx={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", bgcolor: isPayout ? "success.main" : "transparent", pointerEvents: "none" }} />
          </Box>
        }
      >
        <PickersDay {...other} day={day} outsideCurrentMonth={outsideCurrentMonth} />
      </Badge>
    );
  });

  const handlePickerChange = (newValue) => {
    const d = dayjs(newValue).format("YYYY-MM-DD");
    setSelectedDate(d);
    getDateTotals(d); getPayrollTotals(); getDateTasks(d); getDatePayouts(d);
  };

  const exportTasks = () => {
    const csv = toCsv(tasks, [
      { label: "Work Date", map: (r) => moment(r.workDate).format("YYYY-MM-DD") },
      { label: "Work Performed", key: "workPerformed" },
      { label: "Time Worked", key: "timeWorked" },
      { label: "Approved Time", map: (r) => (r.timeBilled === null ? "Pending" : r.timeBilled) },
    ]);
    downloadCsv("my-timesheets.csv", csv);
  };

  const isPayroll = localStorage.getItem("isPayroll") === "1";
  const isConsultant = localStorage.getItem("isConsultant") === "1";

  if (!isPayroll && !isConsultant) {
    return (
      <Card sx={{ p: 3, maxWidth: 520, mx: "auto", textAlign: "center" }}>
        <Typography sx={{ color: "var(--muted)" }}>You have no timesheet access. Return to the dashboard.</Typography>
      </Card>
    );
  }

  const editable = timesheetData?.timeBilled === null || timesheetData?.timeBilled === "";
  const isNew = timesheetData?.id === null;
  const dialogTitle = isNew ? "Add Timesheet" : editable ? "Update Timesheet" : "View Timesheet";

  return (
    <Box>
      {/* Create / Update / View Timesheet */}
      <Dialog open={createTimesheetOpen} onClose={openCreateTimesheet} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {dialogTitle}
          {!isNew && editable && (
            <Tooltip title="Delete timesheet">
              <IconButton aria-label="delete" onClick={openConfirmDeleteBox} sx={{ color: "var(--fail)" }}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {timesheetData?.workDate && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  disabled={!editable}
                  slotProps={{ textField: { fullWidth: true } }}
                  sx={{ mt: 1, mb: 1 }}
                  label="Work Date"
                  format="YYYY-MM-DD"
                  value={dayjs(timesheetData.workDate)}
                  onChange={(e) => setTimeSheetData({ ...timesheetData, workDate: e === null ? "" : moment(e.$d).format("YYYY-MM-DD") })}
                />
              </LocalizationProvider>
            )}
            <TextField disabled={!editable} fullWidth margin="dense" label="Work Performed" multiline rows={4} variant="outlined" type="text" defaultValue={timesheetData?.workPerformed} onChange={(e) => setTimeSheetData({ ...timesheetData, workPerformed: e.target.value })} />
            <TextField disabled={!editable} fullWidth margin="dense" label="Time Worked" variant="outlined" type="number" defaultValue={timesheetData?.timeWorked} onChange={(e) => setTimeSheetData({ ...timesheetData, timeWorked: e.target.value })} />
            {!isNew && (
              <TextField disabled fullWidth margin="dense" label="Approved Time" variant="outlined" value={timesheetData?.timeBilled === null || timesheetData?.timeBilled === "" ? "Pending" : timesheetData?.timeBilled} />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={openCreateTimesheet}>Cancel</Button>
          {isNew ? (
            <Button variant="contained" onClick={createTimesheet}>Create</Button>
          ) : (
            <Button variant="contained" disabled={!editable} onClick={openConfirmUpdateBox}>Update</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={confirmDeleteBoxOpen} onClose={openConfirmDeleteBox} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Delete Timesheet</DialogTitle>
        <DialogActions>
          <Button color="inherit" onClick={openConfirmDeleteBox}>Cancel</Button>
          <Button variant="contained" color="error" onClick={deleteTimesheet}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Update */}
      <Dialog open={confirmUpdateBoxOpen} onClose={openConfirmUpdateBox} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Update Timesheet</DialogTitle>
        <DialogActions>
          <Button color="inherit" onClick={openConfirmUpdateBox}>Cancel</Button>
          <Button variant="contained" onClick={updateTimesheet}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Message */}
      <Dialog open={confirmMessagePopup} onClose={openConfirmMessage} maxWidth="xs" fullWidth>
        <DialogContent><Typography>{confirmMessage}</Typography></DialogContent>
        <DialogActions><Button variant="contained" onClick={openConfirmMessage}>OK</Button></DialogActions>
      </Dialog>

      <Card sx={{ p: { xs: 2, md: 3 }, maxWidth: 640, mx: "auto" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>My Timesheets</Typography>

        {/* Totals */}
        {isConsultant && (
          <Box sx={{ mb: 2 }}>
            <TotalRow label="Approved Task Totals" value={fmtMoney(totals[0]?.sum)} />
            <TotalRow label="Payout Totals" value={fmtMoney(totals[1]?.sum)} />
            <TotalRow label="Balance" value={fmtMoney((totals[0]?.sum ?? 0) - (totals[1]?.sum ?? 0))} strong />
          </Box>
        )}
        {isPayroll && (
          <Box sx={{ mb: 2 }}>
            <TotalRow label={`${payrollTotals[1]?.workWeek || "Last week"} Total Hours`} value={payrollTotals[1]?.hoursWorkedBilled ?? 0} />
            <TotalRow label={`${payrollTotals[0]?.workWeek || "This week"} Total Hours`} value={payrollTotals[0]?.hoursWorkedBilled ?? 0} strong />
          </Box>
        )}

        {/* Calendar - fixed square day cells (so the badge dots stay in the corner),
            spread across the width with space-between, capped at 400 and centered so
            it fills nicely on mobile but never gets oversized on desktop. */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ width: "100%", maxWidth: 400, mx: "auto" }}>
            <StaticDatePicker
              value={dayjs(selectedDate)}
              onChange={handlePickerChange}
              reduceAnimations
              slots={{ actionBar: () => null, day: CustomDay }}
              sx={{
                width: "100%",
                background: "transparent",
                // Stretch the calendar root to the (capped) container instead of its fixed 320px.
                "& .MuiDateCalendar-root": { width: "100%", maxWidth: "100%" },
                "& .MuiPickersCalendarHeader-root": { px: 0 },
                // Header (weekday labels) + each week row spread their 7 fixed cells full-width.
                "& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer": {
                  display: "flex",
                  justifyContent: "space-between",
                  margin: 0,
                },
                // Fixed square cells - the day button (and its wrapping Badge) keep their
                // natural circular size, so the task/payout dots anchor correctly again.
                "& .MuiDayCalendar-weekDayLabel, & .MuiPickersDay-root": {
                  width: 44,
                  height: 44,
                  margin: 0,
                  fontSize: 15,
                },
                "& .MuiDayCalendar-slideTransition": { minHeight: 290 },
              }}
            />
          </Box>
        </LocalizationProvider>

        {/* Actions */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setTimeSheetData({ ...timesheetData, id: null, workDate: selectedDate, workPerformed: "", timeWorked: "", timeBilled: "" });
              openCreateTimesheet();
            }}
          >
            Add Timesheet
          </Button>
          <Button variant="outlined" onClick={() => { getTasks(); getPayouts(); getTotals(); getPayrollTotals(); }}>View All</Button>
          <Box sx={{ flex: 1 }} />
          <Button startIcon={<DownloadIcon />} onClick={exportTasks} disabled={!tasks.length}>Export CSV</Button>
        </Box>

        {/* Tasks */}
        <Typography sx={{ fontWeight: 700, mb: 1 }}>
          {tasks.length ? `Tasks (${tasks.length})` : "No tasks for selected date"}
        </Typography>
        <Box sx={{ border: "1px solid var(--border)", borderRadius: "var(--r-sm)", overflow: "hidden" }}>
          {taskListLoaded && tasks.map((task, index) => {
            const approved = task.timeBilled !== null && task.timeBilled !== "";
            return (
              <Box
                key={index}
                onClick={() => { setTimeSheetData({ ...timesheetData, id: task.id, workDate: task.workDate, workPerformed: task.workPerformed, timeWorked: task.timeWorked, timeBilled: task.timeBilled }); openCreateTimesheet(); }}
                sx={{ px: 2, py: 1.5, borderBottom: "1px solid var(--border)", cursor: "pointer", "&:hover": { background: "var(--surface-2)" } }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography className="app-mono" sx={{ fontWeight: 700 }}>{moment(task.workDate).format("DD-MM-YYYY")}</Typography>
                  <StatusChip domain="approval" value={approved ? "APPROVED" : "PENDING"} dot />
                </Box>
                <Typography sx={{ fontSize: 14, mb: 0.5 }}>{task.workPerformed}</Typography>
                <Box sx={{ display: "flex", gap: 3, color: "var(--muted)", fontSize: 13 }}>
                  <span>Worked: <b className="app-mono">{task.timeWorked}</b></span>
                  <span>Approved: <b className="app-mono">{approved ? task.timeBilled : "Pending"}</b></span>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Payouts (consultant only) */}
        {isConsultant && (
          <>
            <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
              {payouts.length ? `Payouts (${payouts.length})` : "No payouts for selected date"}
            </Typography>
            <Box sx={{ border: "1px solid var(--border)", borderRadius: "var(--r-sm)", overflow: "hidden" }}>
              {payoutListLoaded && payouts.map((p, index) => (
                <Box key={index} sx={{ px: 2, py: 1.5, borderBottom: "1px solid var(--border)" }}>
                  <Typography className="app-mono" sx={{ fontWeight: 700 }}>{moment(p.updatedAt).format("DD-MM-YYYY")}</Typography>
                  <Typography sx={{ fontSize: 14 }}>{p.payoutComment}</Typography>
                  <Typography sx={{ fontSize: 13, color: "var(--muted)" }}>Amount: <b className="app-mono">{fmtMoney(p.payoutAmount)}</b></Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Card>
    </Box>
  );
}

export default ViewMyTimesheets;
